/**
 * Permission Gate Components - Production Grade
 * 
 * UI components for role-based rendering and permission gating.
 * Patterns: Hide / Disable+Explain / Approval Path / SoD Conflict / Snapshot Lock
 */

import React from 'react';
import { Lock, AlertTriangle, ArrowUpRight, Shield, Info, ShieldAlert, Users } from 'lucide-react';
import { usePermissions, useGatedAction, useSnapshotLock } from '../hooks/usePermissions';

// =============================================================================
// GATED BUTTON - Core permission-aware button
// =============================================================================

export function GatedButton({ 
  action, 
  resource, 
  context,
  children, 
  className = '',
  onClick,
  disabled: externalDisabled = false,
  ...props 
}) {
  const permission = useGatedAction(action, resource, context);

  // Loading state
  if (permission.loading) {
    return (
      <button 
        className={`opacity-50 cursor-wait ${className}`}
        disabled
        {...props}
      >
        {children}
      </button>
    );
  }

  // Fully allowed
  if (permission.allowed && !externalDisabled) {
    return (
      <button 
        className={className}
        onClick={onClick}
        {...props}
      >
        {children}
      </button>
    );
  }

  // Snapshot locked
  if (permission.isSnapshotLocked) {
    return (
      <div className="relative group">
        <button 
          className={`${className} opacity-40 cursor-not-allowed`}
          disabled
          {...props}
        >
          <Lock className="w-3 h-3 mr-1 inline" />
          {children}
        </button>
        <Tooltip>Snapshot is locked - read-only mode</Tooltip>
      </div>
    );
  }

  // SoD violation
  if (permission.sodViolation) {
    return (
      <div className="relative group">
        <button 
          className={`${className} opacity-40 cursor-not-allowed border-red-500/30`}
          disabled
          {...props}
        >
          <ShieldAlert className="w-3 h-3 mr-1 inline text-red-400" />
          {children}
        </button>
        <Tooltip className="text-red-400">{permission.tooltip || 'Separation of duties violation'}</Tooltip>
      </div>
    );
  }

  // Requires approval - show with approval path
  if (permission.showApprovalPath) {
    return (
      <div className="relative group">
        <button 
          className={`${className} opacity-70 border-dashed border-amber-500/30`}
          onClick={() => {
            console.log('Request approval for', action, resource);
            // Could open approval modal
          }}
          {...props}
        >
          {children}
          <ArrowUpRight className="w-3 h-3 ml-1 inline text-amber-400" />
        </button>
        <Tooltip className="text-amber-400">
          Requires {permission.approvalRole?.replace(/_/g, ' ').toLowerCase()} approval
        </Tooltip>
      </div>
    );
  }

  // Requires dual approval
  if (permission.showDualApproval) {
    return (
      <div className="relative group">
        <button 
          className={`${className} opacity-40 cursor-not-allowed`}
          disabled
          {...props}
        >
          <Users className="w-3 h-3 mr-1 inline" />
          {children}
        </button>
        <Tooltip>{permission.tooltip || 'Requires dual approval'}</Tooltip>
      </div>
    );
  }

  // Disabled with explanation
  return (
    <div className="relative group">
      <button 
        className={`${className} opacity-40 cursor-not-allowed`}
        disabled
        {...props}
      >
        <Lock className="w-3 h-3 mr-1 inline" />
        {children}
      </button>
      <Tooltip>{permission.tooltip || 'Insufficient permissions'}</Tooltip>
    </div>
  );
}

// =============================================================================
// PERMISSION GATE - Wrapper for conditional rendering
// =============================================================================

export function PermissionGate({ 
  action, 
  resource, 
  context,
  fallback = null,
  showDisabled = false,
  children 
}) {
  const { can, loading } = usePermissions();

  if (loading) {
    return null;
  }

  const result = can(action, resource, context);

  if (result.allowed) {
    return children;
  }

  if (showDisabled && result.requiresApproval) {
    return (
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
    );
  }

  return fallback;
}

// =============================================================================
// ROLE GATE - Render based on roles
// =============================================================================

export function RoleGate({ 
  roles, 
  fallback = null,
  children 
}) {
  const { hasRole, loading } = usePermissions();

  if (loading) {
    return null;
  }

  if (hasRole(roles)) {
    return children;
  }

  return fallback;
}

// =============================================================================
// BOARD GATE - Hide from board members (leadership)
// =============================================================================

export function BoardGate({ 
  children,
  fallback = null,
  showSummary = false,
  summaryComponent = null
}) {
  const { isBoardView, loading } = usePermissions();

  if (loading) return null;

  if (isBoardView) {
    if (showSummary && summaryComponent) {
      return summaryComponent;
    }
    return fallback;
  }

  return children;
}

// =============================================================================
// MASKED FIELD - For sensitive data
// =============================================================================

export function MaskedField({ 
  fieldType, 
  value, 
  context = {},
  showSummary = false,
  summaryValue = null 
}) {
  const { shouldMask, loading } = usePermissions();

  if (loading) {
    return <span className="animate-pulse bg-[#2F2F2F] rounded w-16 h-4 inline-block" />;
  }

  if (shouldMask(fieldType, context)) {
    if (showSummary && summaryValue !== null) {
      return (
        <span className="text-[#6B6B6B]">
          {summaryValue}
        </span>
      );
    }
    return (
      <span className="text-[#6B6B6B] flex items-center gap-1">
        <Lock className="w-3 h-3" />
        <span className="text-xs">Restricted</span>
      </span>
    );
  }

  return <span>{value}</span>;
}

// =============================================================================
// SOD CONFLICT BANNER
// =============================================================================

export function SoDConflictBanner({ rule, onDismiss }) {
  const messages = {
    cannotValidateEvidenceUploadedBySelf: 
      'Separation of duties: You cannot validate evidence you uploaded.',
    cannotApproveOwnWork:
      'Separation of duties: You cannot approve your own work.'
  };

  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
      <ShieldAlert className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm text-red-400 font-medium">Access Restricted</p>
        <p className="text-sm text-[#A0A0A0] mt-1">
          {messages[rule] || 'This action is restricted by compliance policy.'}
        </p>
      </div>
      {onDismiss && (
        <button 
          onClick={onDismiss}
          className="text-[#6B6B6B] hover:text-white"
        >
          ✕
        </button>
      )}
    </div>
  );
}

// =============================================================================
// SNAPSHOT LOCK BANNER
// =============================================================================

export function SnapshotLockBanner({ snapshotId, lockedBy, onClose }) {
  const { canUnlock } = useSnapshotLock();

  return (
    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-start gap-3">
      <Lock className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm text-blue-400 font-medium">Snapshot Locked</p>
        <p className="text-sm text-[#A0A0A0] mt-1">
          This view represents locked snapshot <span className="font-mono text-blue-300">{snapshotId}</span>. 
          Live system may have advanced.
        </p>
        {lockedBy && (
          <p className="text-xs text-[#6B6B6B] mt-1">Locked by: {lockedBy}</p>
        )}
      </div>
      {canUnlock && (
        <button className="text-xs text-blue-400 hover:text-blue-300 border border-blue-500/30 px-2 py-1 rounded">
          Request Unlock
        </button>
      )}
    </div>
  );
}

// =============================================================================
// ROLE BADGE
// =============================================================================

export function RoleBadge({ compact = false }) {
  const { primaryRole, roleInfo, loading } = usePermissions();

  if (loading || !roleInfo) {
    return null;
  }

  const colorClasses = {
    purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
    cyan: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
    slate: 'bg-slate-500/10 border-slate-500/30 text-slate-400',
    gray: 'bg-gray-500/10 border-gray-500/30 text-gray-400'
  };

  const colorClass = colorClasses[roleInfo.color] || colorClasses.gray;

  if (compact) {
    return (
      <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded border ${colorClass}`}>
        {roleInfo.tier}
      </span>
    );
  }

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${colorClass}`}>
      <Shield className="w-4 h-4" />
      <div>
        <p className="text-xs font-medium">{roleInfo.label}</p>
        <p className="text-[10px] opacity-70">{roleInfo.tier}</p>
      </div>
    </div>
  );
}

// =============================================================================
// BOARD VIEW INDICATOR
// =============================================================================

export function BoardViewIndicator() {
  const { isBoardView, loading } = usePermissions();

  if (loading || !isBoardView) {
    return null;
  }

  return (
    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
      <Info className="w-4 h-4 text-purple-400" />
      <span className="text-xs text-purple-400">
        Board View - Summary data only
      </span>
    </div>
  );
}

// =============================================================================
// DRILL GATE - Control drill-down capability
// =============================================================================

export function DrillGate({ 
  resource,
  onClick,
  children,
  fallback = null 
}) {
  const { can, loading, isBoardView } = usePermissions();

  if (loading) return children;

  // Board view cannot drill
  if (isBoardView) {
    return fallback || (
      <div className="cursor-default" title="Drill-down not available in Board View">
        {children}
      </div>
    );
  }

  const result = can('DRILL', resource);

  if (!result.allowed) {
    return fallback || (
      <div className="cursor-default" title={result.reason}>
        {children}
      </div>
    );
  }

  return (
    <div className="cursor-pointer hover:opacity-80" onClick={onClick}>
      {children}
    </div>
  );
}

// =============================================================================
// ACTION MENU WITH PERMISSIONS
// =============================================================================

export function PermissionActionMenu({ resource, actions, onAction, context }) {
  const { can } = usePermissions();

  const visibleActions = actions.filter(action => {
    const result = can(action.action, resource, context);
    return result.allowed || result.requiresApproval;
  });

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      {visibleActions.map((action) => {
        const result = can(action.action, resource, context);
        
        return (
          <button
            key={action.action}
            onClick={() => result.allowed && onAction(action)}
            disabled={!result.allowed}
            className={`w-full text-left px-3 py-2 rounded text-sm transition-colors flex items-center justify-between ${
              result.allowed 
                ? 'hover:bg-[#1F1F1F] text-[#A0A0A0] hover:text-white' 
                : 'opacity-50 cursor-not-allowed text-[#6B6B6B]'
            }`}
          >
            <span className="flex items-center gap-2">
              {action.icon && <action.icon className="w-4 h-4" />}
              {action.label}
            </span>
            {result.requiresApproval && (
              <span className="text-[10px] text-amber-400">Needs approval</span>
            )}
            {!result.allowed && !result.requiresApproval && (
              <Lock className="w-3 h-3" />
            )}
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// HELPER: TOOLTIP
// =============================================================================

function Tooltip({ children, className = '' }) {
  return (
    <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-[#1F1F1F] border border-[#3F3F3F] rounded text-xs text-[#A0A0A0] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 ${className}`}>
      {children}
    </div>
  );
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  GatedButton,
  PermissionGate,
  RoleGate,
  BoardGate,
  DrillGate,
  MaskedField,
  SoDConflictBanner,
  SnapshotLockBanner,
  RoleBadge,
  BoardViewIndicator,
  PermissionActionMenu
};
